/**
 * AIEditTab Component
 *
 * Wrapper for AI Image Editing functionality within the Image section tabs.
 */

import React, { useState, useCallback, useEffect, memo } from 'react';
import {
  Play,
  RefreshCw,
  Download,
  Wand2,
  ImageIcon,
  Coins,
  Sparkles,
  Zap,
  Award,
  Loader2,
} from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useImageEditing } from '@/hooks/useImageEditing';
import { useToast } from '@/hooks/useToast';
import { useRequireAuth } from '@/hooks/useRequireAuth';
import { MediaUploader } from '@/components/common/MediaUploader';
import { ToastContainer } from '@/components/Toast';
import { LoginModal } from '@/components/auth';
import {
  deriveEditOutputSize,
  type ImageEditProvider,
  type ImageEditRequest,
} from '@/services/imageEditing';
import { useImageEditModels, type ImageEditModelOption } from '../imageEditModels';
import { blobToDataUrl } from '@/utils/downloadProxy';
import { isDesktopApp } from '@/utils/apiMode';
import { saveMediaFile } from '@/utils/electronBridge';
import { getApiBaseUrl } from '@/services/urlResolver';

interface AIEditTabProps {
  /**
   * When provided, the editing provider is controlled by the parent (the
   * unified Image Edit tab) and the in-tab provider grid is hidden. Omit for
   * standalone usage.
   */
  provider?: ImageEditProvider;
  /** Hide the internal provider selector grid (used when controlled). */
  hideProviderSelector?: boolean;
  /**
   * Starter instruction inserted into the textarea whenever `presetNonce`
   * changes (the unified tab seeds this when an operation chip is clicked).
   */
  instructionPreset?: string;
  /** Bumped by the parent on each operation-chip click to (re)apply the preset. */
  presetNonce?: number;
  /** Help line shown under the instruction box for the selected operation. */
  operationHint?: string;
}

export const AIEditTab: React.FC<AIEditTabProps> = memo(function AIEditTab({
  provider: controlledProvider,
  hideProviderSelector = false,
  instructionPreset,
  presetNonce,
  operationHint,
}: AIEditTabProps) {
  const { t } = useTranslation();
  const {
    isEditing,
    progress,
    message,
    error,
    resultUrl,
    tokens: billedTokens,
    edit,
    reset,
  } = useImageEditing();
  const { toasts, showToast, removeToast } = useToast();
  const { isLoginModalOpen, loginMessage, closeLoginModal, requireAuth } = useRequireAuth();

  // Form state
  const [sourceImage, setSourceImage] = useState<File | null>(null);
  const [sourceImageBase64, setSourceImageBase64] = useState<string | null>(null);
  const [sourceImagePreview, setSourceImagePreview] = useState<string | null>(null);
  // Natural dimensions of the source, used to keep the edit output's aspect
  // ratio (instead of the providers' hard-coded square 1024*1024 default).
  const [sourceDims, setSourceDims] = useState<{ width: number; height: number } | null>(null);
  const [instruction, setInstruction] = useState('');
  // The editors, their prices and WHICH ONE IS DEFAULT all come from
  // GET /api/v1/image-editing/providers. 'qwen-edit-2511' used to be written
  // out here twice as the default; the server publishes it (IMAGE_EDIT_DEFAULT_
  // PROVIDER) and a second opinion about it is one more thing to drift.
  const {
    cloudModels,
    defaultProviderId,
    isLoading: isCatalogLoading,
    error: catalogError,
    refresh: refreshCatalog,
  } = useImageEditModels();
  // null until the catalog resolves a default (or the parent names one).
  const [provider, setProvider] = useState<ImageEditProvider | null>(controlledProvider ?? null);

  // Keep the provider in sync when the parent controls it; otherwise adopt the
  // server's default as soon as it is known.
  useEffect(() => {
    if (controlledProvider) {
      setProvider(controlledProvider);
      return;
    }
    setProvider((current) => current ?? defaultProviderId);
  }, [controlledProvider, defaultProviderId]);

  // Seed the instruction with the operation's starter template each time the
  // parent bumps the nonce (i.e. an operation chip was clicked). Nonce 0 is the
  // initial render — left untouched so a freshly-picked model starts blank.
  useEffect(() => {
    if (presetNonce && presetNonce > 0) {
      setInstruction(instructionPreset ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetNonce]);

  // Handle image selection
  const handleImageSelect = useCallback((file: File, _mediaType: string, base64?: string) => {
    setSourceImage(file);
    setSourceImageBase64(base64 || null);
    setSourceDims(null);
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setSourceImagePreview(previewUrl);
    // Read natural dimensions to preserve aspect ratio on output.
    const probe = new Image();
    probe.onload = () => {
      setSourceDims({ width: probe.naturalWidth, height: probe.naturalHeight });
    };
    probe.src = previewUrl;
  }, []);

  // Handle image removal
  const handleImageRemove = useCallback(() => {
    if (sourceImagePreview) {
      URL.revokeObjectURL(sourceImagePreview);
    }
    setSourceImage(null);
    setSourceImageBase64(null);
    setSourceImagePreview(null);
    setSourceDims(null);
  }, [sourceImagePreview]);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!sourceImageBase64 || !instruction.trim()) {
      showToast('error', 'Missing Input', 'Please upload an image and enter edit instructions');
      return;
    }
    if (!provider) {
      // Unreachable through the button (it is disabled), but an edit whose
      // price we could not load must never be submitted anyway -- the user
      // would be billed an amount the UI never showed them.
      showToast(
        'error',
        'Price unavailable',
        'The editor list could not be loaded, so this edit has no known price.'
      );
      return;
    }

    requireAuth(
      async () => {
        const request: ImageEditRequest = {
          image_base64: sourceImageBase64,
          instruction: instruction.trim(),
          provider,
          // Preserve the source aspect ratio (undefined → backend default).
          size: deriveEditOutputSize(sourceDims?.width, sourceDims?.height),
        };

        showToast('info', 'Starting Edit', 'AI image editing started...');
        await edit(request);
      },
      t('auth.loginRequired.imageEdit', { defaultValue: 'Please sign in to use AI Image Editing' })
    );
  }, [sourceImageBase64, sourceDims, instruction, provider, requireAuth, edit, showToast, t]);

  // Handle reset
  const handleReset = useCallback(() => {
    handleImageRemove();
    setInstruction('');
    setProvider(controlledProvider ?? defaultProviderId);
    reset();
  }, [handleImageRemove, reset, controlledProvider, defaultProviderId]);

  // Handle download via backend proxy (bypasses CORS on external CDN URLs).
  // Desktop saves through the native dialog like the sibling ImageCard does;
  // anchor-clicking a blob in Electron goes to Chromium's download manager,
  // whose Save-As window can open behind the app.
  const handleDownload = useCallback(async () => {
    if (!resultUrl) return;
    try {
      const baseUrl = getApiBaseUrl();
      const proxyUrl = `${baseUrl}/api/v1/image-editing/download?url=${encodeURIComponent(resultUrl)}`;
      const response = await fetch(proxyUrl);
      if (!response.ok) {
        throw new Error(`Download proxy returned ${response.status}`);
      }
      const blob = await response.blob();
      const contentType = blob.type || 'image/png';
      const ext = contentType.includes('jpeg') || contentType.includes('jpg') ? 'jpg' : 'png';
      const filename = `edited-image.${ext}`;

      if (isDesktopApp()) {
        const dataUrl = await blobToDataUrl(blob);
        const result = await saveMediaFile(dataUrl, filename);
        if (!result.success && !result.canceled) {
          throw new Error(result.error ?? 'Save failed');
        }
        return;
      }

      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      // Opening the raw URL used to be the fallback: in Electron that is a
      // blank window, and for a failing proxy it just shows an error body.
      console.error('[AIEditTab] Download failed', err);
    }
  }, [resultUrl]);

  // Get selected provider info. Undefined while the catalog is loading and
  // after it failed -- in both cases the price is genuinely not known yet.
  const selectedProvider = cloudModels.find((p) => p.id === provider);
  const showForm = !isEditing && !resultUrl;
  const showProgress = isEditing;
  const showResult = !isEditing && resultUrl;
  // A cloud edit costs real money. Without a price from the server there is
  // nothing honest to put on the button, so the edit is blocked rather than
  // quoted from a constant (RULE #5) -- that constant is what billed users 57
  // tokens under a "30 tokens" label until 2026-08-28.
  const canSubmit = Boolean(sourceImage && instruction.trim() && selectedProvider);

  // Quality badge color
  const getQualityColor = (quality: string | undefined) => {
    switch (quality) {
      case 'excellent':
        return 'text-purple-600 dark:text-purple-400';
      case 'great':
        return 'text-blue-600 dark:text-blue-400';
      default:
        return 'text-green-600 dark:text-green-400';
    }
  };

  // Speed badge color
  const getSpeedColor = (speed: string | undefined) => {
    switch (speed) {
      case 'fast':
        return 'text-green-600 dark:text-green-400';
      case 'medium':
        return 'text-yellow-600 dark:text-yellow-400';
      default:
        return 'text-orange-600 dark:text-orange-400';
    }
  };

  /**
   * The token price of one editor, or an explicit "unavailable".
   *
   * `pricedBySize` editors (nano-banana-pro-edit) charge more for a larger
   * output than the default-size figure the API publishes, so their number is
   * shown as a floor rather than as the total.
   */
  const renderProviderPrice = (model: ImageEditModelOption) => {
    if (model.tokensPerEdit === undefined) {
      return (
        <span className="text-secondary-500 dark:text-secondary-400">
          <Coins className="w-3 h-3 inline mr-1" />
          {t('imageEditing.priceUnknown', { defaultValue: 'price unavailable' })}
        </span>
      );
    }
    return (
      <span className="text-yellow-600 dark:text-yellow-400">
        <Coins className="w-3 h-3 inline mr-1" />
        {model.pricedBySize
          ? t('imageEditing.tokensFrom', {
              defaultValue: 'from {{tokens}} tokens',
              tokens: String(model.tokensPerEdit),
            })
          : t('imageEditing.tokens', {
              defaultValue: '{{tokens}} tokens',
              tokens: String(model.tokensPerEdit),
            })}
      </span>
    );
  };

  return (
    <div data-testid="ai-edit-tab">
      {/* Hero Section */}
      {showForm && (
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl mb-3">
            <Wand2 className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-xl font-bold text-secondary-900 dark:text-white mb-1">
            {t('imageEditing.title', { defaultValue: 'AI Image Editing' })}
          </h2>
          <p className="text-sm text-secondary-600 dark:text-secondary-400 max-w-lg mx-auto">
            {t('imageEditing.description', {
              defaultValue:
                'Transform your images with AI. Simply describe what you want to change.',
            })}
          </p>
        </div>
      )}

      <div className="space-y-4">
        {/* Configuration Form */}
        {showForm && (
          <>
            {/* Image Upload Section */}
            <div>
              <label className="label-themed">
                <ImageIcon className="w-4 h-4 inline mr-2" />
                {t('imageEditing.sourceImage', { defaultValue: 'Source Image' })}
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <MediaUploader
                    accept={['image']}
                    onFileSelect={handleImageSelect}
                    onRemove={handleImageRemove}
                    includeBase64={true}
                    dropzoneText={t('imageEditing.dropImage', {
                      defaultValue: 'Drop image to edit here',
                    })}
                  />
                </div>
                {sourceImagePreview && (
                  <div className="img-preview-square">
                    <img
                      src={sourceImagePreview}
                      alt="Preview"
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Edit Instruction */}
            <div>
              <label className="label-themed">
                <Sparkles className="w-4 h-4 inline mr-2" />
                {t('imageEditing.instruction', { defaultValue: 'Edit Instruction' })}
              </label>
              <textarea
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                placeholder={t('imageEditing.instructionPlaceholder', {
                  defaultValue: 'Describe what you want to change...',
                })}
                className="w-full px-4 py-3 border border-secondary-200 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white placeholder-secondary-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                rows={3}
              />
              {operationHint && <p className="text-xs text-white/50 mt-1.5">{operationHint}</p>}
            </div>

            {/* Provider Selection (hidden when the unified tab controls it) */}
            {!hideProviderSelector && (
              <div>
                <label className="label-themed">
                  {t('imageEditing.provider', { defaultValue: 'AI Provider' })}
                </label>
                {isCatalogLoading ? (
                  <div
                    role="status"
                    className="flex items-center gap-2 py-4 text-sm text-secondary-500 dark:text-secondary-400"
                    data-testid="ai-edit-providers-loading"
                  >
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('imageEditing.loadingProviders', {
                      defaultValue: 'Loading editors and their prices...',
                    })}
                  </div>
                ) : catalogError ? (
                  <div className="alert-danger" data-testid="ai-edit-providers-error">
                    <p className="text-red-600 dark:text-red-400">
                      {t('imageEditing.providersUnavailable', {
                        defaultValue:
                          "The editor list could not be loaded, so no price is known. Nothing is offered rather than a price we can't stand behind.",
                      })}
                    </p>
                    <p className="mt-1 break-words text-xs text-red-500/80">{catalogError}</p>
                    <button
                      type="button"
                      onClick={refreshCatalog}
                      className="btn-danger mt-2 min-h-[44px]"
                    >
                      <RefreshCw className="w-4 h-4" />
                      {t('common.retry', { defaultValue: 'Try Again' })}
                    </button>
                  </div>
                ) : (
                  <div className="provider-grid">
                    {cloudModels.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        // `cloudModels` is built from the catalog rows, whose id
                        // IS an ImageEditProvider; the widening to `string`
                        // exists only so 'fooocus' can share the same shape.
                        onClick={() => setProvider(p.id as ImageEditProvider)}
                        className={`p-3 rounded-lg border-2 transition-all text-left ${
                          provider === p.id
                            ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                            : 'border-secondary-200 dark:border-secondary-600 hover:border-orange-300'
                        }`}
                      >
                        <div className="font-medium text-secondary-900 dark:text-white">
                          {p.name}
                        </div>
                        <div className="text-xs text-secondary-500 dark:text-secondary-400 mb-2">
                          {p.description}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                          {p.quality && (
                            <span className={getQualityColor(p.quality)}>
                              <Award className="w-3 h-3 inline mr-1" />
                              {p.quality}
                            </span>
                          )}
                          {p.speed && (
                            <span className={getSpeedColor(p.speed)}>
                              <Zap className="w-3 h-3 inline mr-1" />
                              {p.speed}
                            </span>
                          )}
                          {renderProviderPrice(p)}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Generate Button */}
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="w-full py-4 px-6 bg-gradient-to-r from-orange-600 to-red-600 text-white font-semibold rounded-lg hover:from-orange-700 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2"
            >
              <span className="inline-flex items-center gap-2">
                <Play className="w-5 h-5" />
                {t('imageEditing.generate', { defaultValue: 'Edit Image' })}
              </span>
              {/* A price, a spinner, or "price unavailable" -- never a stale number. */}
              {selectedProvider?.tokensPerEdit !== undefined ? (
                <span className="opacity-80 text-sm sm:text-base">
                  {selectedProvider.pricedBySize
                    ? t('imageEditing.tokensFrom', {
                        defaultValue: 'from {{tokens}} tokens',
                        tokens: String(selectedProvider.tokensPerEdit),
                      })
                    : t('imageEditing.tokens', {
                        defaultValue: '{{tokens}} tokens',
                        tokens: String(selectedProvider.tokensPerEdit),
                      })}
                </span>
              ) : isCatalogLoading ? (
                <span className="inline-flex items-center gap-1 opacity-80 text-sm sm:text-base">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('imageEditing.loadingPrice', { defaultValue: 'loading price...' })}
                </span>
              ) : (
                <span className="opacity-80 text-sm sm:text-base">
                  {t('imageEditing.priceUnknown', { defaultValue: 'price unavailable' })}
                </span>
              )}
            </button>
          </>
        )}

        {/* Progress Section */}
        {showProgress && (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full mb-4 animate-pulse">
              <Wand2 className="w-8 h-8 text-orange-600 dark:text-orange-400" />
            </div>
            <h2 className="heading-xl-themed-sm">
              {t('imageEditing.processing', { defaultValue: 'Editing Image...' })}
            </h2>
            <p className="text-secondary-600 dark:text-secondary-400 mb-4">{message}</p>
            <div className="w-full max-w-md mx-auto bg-secondary-200 dark:bg-secondary-700 rounded-full h-3 mb-2">
              <div
                className="bg-gradient-to-r from-orange-500 to-red-500 h-3 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-sm text-secondary-500">{progress}%</p>
          </div>
        )}

        {/* Error Display */}
        {error && !isEditing && (
          <div className="alert-danger">
            <h3 className="text-lg font-semibold text-red-700 dark:text-red-300 mb-2">
              {t('common.error', { defaultValue: 'Error' })}
            </h3>
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <button onClick={handleReset} className="btn-danger">
              <RefreshCw className="w-4 h-4" />
              {t('common.retry', { defaultValue: 'Try Again' })}
            </button>
          </div>
        )}

        {/* Result Display */}
        {showResult && (
          <div>
            <h2 className="heading-xl-themed">
              {t('imageEditing.result', { defaultValue: 'Your Edited Image' })}
            </h2>

            {/* Edit Another - above image */}
            <div className="flex justify-center mb-4">
              <button
                onClick={handleReset}
                className="py-2 px-5 border border-secondary-200 dark:border-secondary-600 text-secondary-700 dark:text-secondary-300 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                {t('imageEditing.editAnother', { defaultValue: 'Edit Another Image' })}
              </button>
            </div>

            {/* Before/After Comparison */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {sourceImagePreview && (
                <div>
                  <p className="text-sm text-secondary-500 dark:text-secondary-400 mb-2">
                    {t('imageEditing.before', { defaultValue: 'Before' })}
                  </p>
                  <div className="img-preview-square">
                    <img
                      src={sourceImagePreview}
                      alt="Original"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              )}
              <div>
                <p className="text-sm text-secondary-500 dark:text-secondary-400 mb-2">
                  {t('imageEditing.after', { defaultValue: 'After' })}
                </p>
                <div className="img-preview-square">
                  <img src={resultUrl} alt="Edited" className="w-full h-full object-contain" />
                </div>
              </div>
            </div>

            {/* Download - below image */}
            <div className="flex flex-col items-center gap-3">
              <button
                onClick={handleDownload}
                className="w-full max-w-md py-4 px-8 bg-orange-600 text-white text-lg font-semibold rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center gap-2"
              >
                <Download className="w-6 h-6" />
                {t('common.download', { defaultValue: 'Download' })}
              </button>
              {billedTokens > 0 && (
                <p className="text-sm text-white/50 text-center">
                  {t('imageEditing.totalTokens', { defaultValue: 'Total cost' })}:{' '}
                  <span className="text-green-400 font-medium">
                    {billedTokens.toLocaleString()} tokens
                  </span>
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* Login Modal */}
      <LoginModal isOpen={isLoginModalOpen} onClose={closeLoginModal} message={loginMessage} />
    </div>
  );
});

export default AIEditTab;
